import React, { useState } from "react";
import { Param, ParamTable } from "./Paramtable";
import { EndpointHeader } from "./Endpointheader";
import { CodeBlock } from "./Codeblock";
import { MethodBadge } from "./Methodbadge";

type HeaderType = "text" | "image" | "video" | "document";
type TemplateMode = "simple" | "variable";

// ─── Params ────────────────────────────────────────────────────────────────

const BASE_PARAMS: Param[] = [
  { name: "to", type: "string", required: true, description: "Recipient phone number in E.164 format (e.g. 919113887558)." },
  { name: "templateName", type: "string", required: true, description: "Name of the approved WhatsApp template." },
  { name: "language", type: "string", required: true, description: 'Language code, e.g. "en_US".' },
  { name: "headerType", type: "string", required: true, description: "TEXT, IMAGE, VIDEO, or DOCUMENT." },
  { name: "headerParams", type: "array", required: false, description: "Array of values for header variables (if any)." },
  { name: "bodyParams", type: "array", required: false, description: "Array of values for body variables like {{1}}, {{2}}." },
  { name: "buttonUrlParams", type: "array", required: false, description: "Array of values for dynamic URL buttons." },
  { name: "headerMediaId", type: "string", required: false, description: "Media URL returned from /api/file-upload (required for media headers)." },
];

// ─── Media Upload ───────────────────────────────────────────────────────────

const MEDIA_UPLOAD_CURL = `curl 'https://empedance.com/api/file-upload' \\
  -H 'Authorization: Bearer <your_token>' \\
  -F 'file=@/path/to/your/file'`;

const MEDIA_UPLOAD_RESPONSE = `{
  "message": "File uploaded successfully",
  "file": "https://empedance.sgp1.digitaloceanspaces.com/Whatsaap/1776158911333_142-1.png"
}`;

// ─── Payload Builders ───────────────────────────────────────────────────────

function buildPayload(
  headerType: HeaderType,
  mode: TemplateMode
): string {
  const isVariable = mode === "variable";
  const isMedia = headerType !== "text";

  return `{
  "to": "919113887558",
  "templateName": "${isVariable ? "ippo_otp" : "text_header_template"}",
  "language": "en_US",
  "headerParams": [],
  "bodyParams": ${isVariable ? '["1234"]' : "[]"},
  "buttonUrlParams": [],
  ${isMedia ? `"headerMediaId": "https://empedance.sgp1.digitaloceanspaces.com/Whatsaap/your-uploaded-file.png",` : ""}
  "headerType": "${headerType.toUpperCase()}"
}`;
}

function buildCurl(
  headerType: HeaderType,
  mode: TemplateMode
): string {
  const isVariable = mode === "variable";
  const isMedia = headerType !== "text";

  const payload = `{
  "to": "919113887558",
  "templateName": "${isVariable ? "ippo_otp" : "text_header_template"}",
  "language": "en_US",
  "headerParams": [],
  "bodyParams": ${isVariable ? '["1234"]' : "[]"},
  "buttonUrlParams": [],
  ${isMedia ? `"headerMediaId": "https://empedance.sgp1.digitaloceanspaces.com/Whatsaap/your-uploaded-file.png",` : ""}
  "headerType": "${headerType.toUpperCase()}"
}`;

  return `curl 'https://apiwhat.empedance.com/api/whatsapp/company/send/template'\\
  -H 'Authorization: Bearer <your_token>' \\
  -H 'Content-Type: application/json' \\
  --data-raw '${payload}'`;
}

// ─── Response ───────────────────────────────────────────────────────────────

const RESPONSE_SAMPLE = `{
  "status": 1,
  "data": "{\\"messaging_product\\":\\"whatsapp\\",\\"contacts\\":[{\\"input\\":\\"919113887558\\",\\"wa_id\\":\\"919113887558\\"}],\\"messages\\":[{\\"id\\":\\"wamid.HBgMOTE5MTEzODg3NTU4FQIAERgSMjcwMjQzQjA4NTNEOTY2MDhEAA==\\",\\"message_status\\":\\"accepted\\"}]}",
  "error": null
}`;

// ─── Toggle Pill Component ──────────────────────────────────────────────────

export function TogglePill<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { id: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/60">
        {label}
      </p>

      <div className="flex w-fit items-center gap-1 rounded-xl border border-white/[0.08] bg-white/[0.04] p-1 backdrop-blur-[6px]">
        {options.map(({ id, label: optLabel }) => {
          const isActive = value === id;

          return (
            <button
              key={id}
              onClick={() => onChange(id)}
              className={`
                rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200

                ${isActive
                  ? "border border-web-primary/20 bg-web-primary/10 text-web-primary"
                  : "text-white/60 hover:bg-white/[0.04] hover:text-white"
                }
              `}
            >
              {optLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
}
// ─── Options ────────────────────────────────────────────────────────────────

const HEADER_OPTIONS: { id: HeaderType; label: string }[] = [
  { id: "text", label: "Text" },
  { id: "image", label: "Image" },
  { id: "video", label: "Video" },
  { id: "document", label: "Document" },
];

const MODE_OPTIONS: { id: TemplateMode; label: string }[] = [
  { id: "simple", label: "Simple" },
  { id: "variable", label: "Variable" },        // Fixed: Removed {{1}} from label to avoid JSX parsing error
];

// ─── Main Component ─────────────────────────────────────────────────────────

export const SendSingleSection: React.FC = () => {
  const [headerType, setHeaderType] = useState<HeaderType>("text");
  const [mode, setMode] = useState<TemplateMode>("simple");

  const isMediaType = headerType !== "text";
  const isVariable = mode === "variable";

  return (
    <div>
      {/* Title */}
      <div className="flex items-center gap-3 mb-2">
        <MethodBadge method="POST" />
        <h1 className="text-2xl font-bold text-white">Send Single Template Message</h1>
      </div>

      <p className="text-white/70 text-sm mt-1 mb-6">
        Send an approved WhatsApp template message to a single recipient.
      </p>

      <EndpointHeader method="POST" url="/api/whatsapp/company/send/template" />

      {/* Toggles */}
      <div className="mt-5 flex flex-wrap gap-6">
        <TogglePill
          label="Header Type"
          options={HEADER_OPTIONS}
          value={headerType}
          onChange={setHeaderType}
        />

        <TogglePill
          label="Template Mode"
          options={MODE_OPTIONS}
          value={mode}
          onChange={setMode}
        />
      </div>

      {/* Variable Mode Explainer */}
      {isVariable && (
        <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
          <p className="text-sm font-semibold text-indigo-800 mb-1">
            🔢 Variable Template
          </p>
          <p className="text-xs text-indigo-700">
            Use placeholders like <code className="bg-indigo-100 px-1 rounded">{"{{1}}"}</code>,{" "}
            <code className="bg-indigo-100 px-1 rounded">{"{{2}}"}</code> in the template body.
          </p>
          <p className="text-xs text-indigo-700 mt-2">
            Pass the replacement values in <code>bodyParams</code>.
            Example: <code>{'bodyParams: ["1234"]'}</code> for{" "}
            <code>{'Your OTP is {{1}}'}</code>
          </p>
        </div>
      )}

      {/* Media Upload Section */}
      {isMediaType && (
        <div className="mt-5 p-4 border border-white/[0.08] bg-white/[0.04] backdrop-blur-[6px] rounded-xl">
          <p className="text-sm font-semibold text-white mb-1">
            📎 Step 1 — Upload your {headerType} first
          </p>
          <p className="text-xs text-white/70 mb-3">
            Upload the file and use the returned URL as{" "}
            <code className="text-white px-1 rounded">headerMediaId</code>.
          </p>
          <CodeBlock
            title="POST https://empedance.com/api/file-upload"
            variants={[{ label: "cURL", value: MEDIA_UPLOAD_CURL }]}
          />
          <CodeBlock
            title="RESPONSE 200 · OK"
            variants={[{ label: "JSON", value: MEDIA_UPLOAD_RESPONSE }]}
          />
        </div>
      )}

      {/* Params Table */}
      <ParamTable title="Request Body" params={BASE_PARAMS} />

      {/* Request Example */}
      <CodeBlock
        title="Request Example"
        variants={[
          { label: "JSON", value: buildPayload(headerType, mode) },
          { label: "cURL", value: buildCurl(headerType, mode) },
        ]}
      />

      {/* Response */}
      <CodeBlock
        title="RESPONSE 200 · OK"
        variants={[{ label: "JSON", value: RESPONSE_SAMPLE }]}
      />

      {/* Note */}
      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
        <span className="font-semibold">Note:</span> For Image, Video, or Document templates,
        you must first upload the file using <code>/api/file-upload</code> and pass the returned URL in <code>headerMediaId</code>.
      </div>
    </div>
  );
};